<template>
  <div
    v-if="loading"
    class="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible bg-white dark:bg-gray-900 transition-colors duration-300"
  >
    <svg
      class="w-12 h-12 md:w-16 md:h-16 animate-spin text-gray-900/50 dark:text-gray-200/50"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
        stroke="currentColor"
        stroke-width="5"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></path>
      <path
        d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
        stroke="currentColor"
        stroke-width="5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-gray-900 dark:text-gray-200"
      ></path>
    </svg>
  </div>
  <div v-else class="flex flex-col gap-5">
    <div
      class="relative flex flex-col w-full h-min text-main-700 bg-white rounded-lg bg-clip-border p-4 gap-5 shadow-sm dark:bg-gray-800 dark:text-gray-200"
    >
      <!-- Navigation -->
      <nav class="w-full flex flex-col md:flex-row justify-between gap-4">
        <!-- Left Section: Back Button and Order ID -->
        <div class="flex items-center gap-4">
          <!-- Back Button -->
          <button
            @click="goback"
            class="p-3 flex items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border border-blue-gray-600/10 rounded-lg transition-all duration-300 ease-in-out dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
            aria-label="Go back"
          >
            <i
              class="fa-solid fa-arrow-left text-base text-gray-800 dark:text-gray-200"
            ></i>
          </button>

          <!-- Order ID -->
          <h3
            class="text-lg md:text-xl font-medium truncate dark:text-gray-200"
          >
            Order -
            <span class="text-gray-800 dark:text-gray-200"
              >#{{ order.order_id }}</span
            >
          </h3>
        </div>

        <!-- Right Section: Order Date -->
        <div class="flex items-center justify-end md:justify-start">
          <span class="text-sm md:text-base text-gray-600 dark:text-gray-300">
            {{ formatDate(order.order_date) }}
          </span>
        </div>
      </nav>
    </div>
    <div
      class="relative flex flex-col w-full h-full text-gray-700 bg-transparent overflow-auto"
    >
      <div
        class="flex flex-col justify-start items-start w-full space-y-4 md:space-y-6 xl:space-y-8"
      >
        <div class="flex justify-between w-full h-full gap-2 portrait:flex-col">
          <div
            class="w-full flex flex-col h-full rounded-lg relative gap-2 portrait:order-2"
          >
            <div
              class="w-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 flex flex-col h-full"
            >
              <!-- Header -->
              <p
                class="text-lg font-semibold text-gray-800 dark:text-white p-6"
              >
                Customer’s Cart ({{ order?.cart_data?.length }})
              </p>

              <!-- Cart Items -->
              <div class="px-6 mb-4 flex flex-wrap gap-4">
                <div
                  v-for="(item, index) in order?.cart_data"
                  :key="index"
                  class="w-full xl:w-[calc(50%-1rem)] p-2 min-w-[250px]"
                >
                  <a
                    :href="item.link"
                    class="w-full flex flex-row gap-4 min-h-[96px] rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
                  >
                    <!-- Product Image -->
                    <div class="w-2/5 h-full flex-shrink-0">
                      <img
                        class="w-full h-full rounded-lg object-cover object-left"
                        :src="getFileUrl(item.image)"
                        :alt="item.name"
                      />
                    </div>

                    <!-- Product Details -->
                    <div
                      class="flex flex-col justify-between p-3 w-3/5 overflow-hidden"
                    >
                      <!-- Product Name and Color -->
                      <div class="overflow-hidden">
                        <h3
                          class="text-sm font-medium text-gray-800 dark:text-white truncate"
                        >
                          {{ item.name }}
                        </h3>
                        <p
                          class="text-xs text-gray-600 dark:text-gray-300 mt-1 truncate"
                        >
                          Color: {{ item.color || "N/A" }}
                        </p>
                      </div>

                      <!-- Price -->
                      <div class="flex items-center gap-2 overflow-hidden">
                        <template
                          v-if="Number(item.discounted_cost !== item.real_cost)"
                        >
                          <span
                            class="text-xs line-through text-gray-500 dark:text-gray-400 truncate"
                          >
                            {{ formatPrice(item.real_cost)
                            }}{{ order.currency }}
                          </span>
                          <span
                            class="text-sm font-semibold text-gray-800 dark:text-white truncate"
                          >
                            {{ formatPrice(item.discounted_cost)
                            }}{{ order.currency }}
                          </span>
                        </template>
                        <template v-else>
                          <span
                            class="text-sm font-semibold text-gray-800 dark:text-white truncate"
                          >
                            {{ formatPrice(item.discounted_cost)
                            }}{{ order.currency }}
                          </span>
                        </template>
                      </div>

                      <!-- Quantity and Size -->
                      <div
                        class="flex justify-between text-xs text-gray-600 dark:text-gray-300 truncate"
                      >
                        <span>Quan: {{ item.quantity }}</span>
                        <span>Size: {{ item.size }}</span>
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            <div
              class="w-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 flex flex-col h-full p-6"
            >
              <!-- Header -->
              <p
                class="text-lg font-semibold text-gray-800 dark:text-white mb-4"
              >
                Payment Details
              </p>

              <!-- Payment Status and Details -->
              <div class="flex flex-col xl:flex-row lg:flex-col gap-4">
                <!-- Payment Status -->
                <div class="w-full xl:w-1/2 p-2">
                  <div class="flex items-center justify-between mb-4">
                    <h2
                      class="text-base font-medium text-gray-800 dark:text-white"
                    >
                      Payment Status
                    </h2>
                    <span
                      class="px-4 py-1 text-sm font-medium rounded-lg"
                      :class="
                        order.status === 1
                          ? 'text-green-900 bg-green-500/10 dark:text-green-500 dark:bg-green-500/10'
                          : 'text-red-900 bg-red-500/10 dark:text-red-500 dark:bg-red-500/10'
                      "
                    >
                      {{ order.status === 1 ? "Paid" : "Unpaid" }}
                    </span>
                  </div>
                  <button
                    @click.prevent="confirmPayment(order.id)"
                    class="w-full py-3 rounded-lg font-medium text-white transition-all focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 flex items-center justify-center"
                    :class="
                      order.status === 1 || isLoading
                        ? 'bg-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    "
                    :disabled="order.status === 1 || isLoading"
                  >
                    <svg
                      v-if="isLoading"
                      class="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      ></path>
                    </svg>
                    <span v-else>Confirm Payment</span>
                  </button>
                </div>

                <!-- Payment Details -->
                <div class="w-full xl:w-1/2 p-2">
                  <div
                    class="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg w-full"
                  >
                    <h3
                      class="text-lg font-semibold text-gray-800 dark:text-white mb-2"
                    >
                      Payment Details
                    </h3>
                    <div class="flex justify-between">
                      <span class="text-sm text-gray-600 dark:text-gray-300"
                        >Amount Paid:</span
                      >
                      <span
                        class="text-sm font-medium text-gray-800 dark:text-white"
                      >
                        {{ formatPrice(order.amount) }} {{ order.currency }}
                      </span>
                    </div>
                    <div class="flex justify-between mt-2">
                      <span class="text-sm text-gray-600 dark:text-gray-300"
                        >Payment Method:</span
                      >
                      <span
                        class="text-sm font-medium text-gray-800 dark:text-white"
                      >
                        {{ order.payment_method }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="w-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 flex flex-col h-full p-6"
            >
              <!-- Header -->
              <p
                class="text-lg font-semibold text-gray-800 dark:text-white mb-4"
              >
                Delivery Details
              </p>

              <!-- Saved Delivery Section -->
              <div
                v-if="deliveries?.length > 0"
                class="flex flex-col gap-4 mb-6"
              >
                <div class="w-full bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p
                    class="text-base font-medium text-gray-800 dark:text-white mb-2"
                  >
                    Saved Delivery
                  </p>
                  <div class="relative w-full">
                    <select
                      v-model="selectedDelivery"
                      @change="updateDelivery"
                      class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-100 bg-transparent focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent appearance-none cursor-pointer"
                    >
                      <!-- Disabled placeholder option -->
                      <option
                        value=""
                        disabled
                        class="text-gray-400 dark:text-gray-100"
                      >
                        Select a delivery
                      </option>

                      <!-- Dynamic delivery options -->
                      <option
                        v-for="(delivery, index) in deliveries"
                        :key="index"
                        :value="delivery.id"
                        class="text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
                      >
                        {{ delivery.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Delivery Form -->
              <form class="flex flex-col gap-4">
                <!-- Delivery Agent and Phone Number -->
                <div class="w-full flex flex-col lg:flex-row gap-4">
                  <!-- Delivery Agent -->
                  <div class="w-full lg:w-1/2">
                    <label
                      class="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                    >
                      Delivery Agent <span class="text-red-500">*</span>
                    </label>
                    <input
                      :disabled="isSubmitting"
                      type="text"
                      v-model="newDelivery.name"
                      placeholder="Abc Delivery"
                      class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-sub-50 focus:border-transparent transition-all duration-200"
                    />
                    <p v-if="errors.name" class="text-red-500 text-sm mt-1">
                      {{ errors.name }}
                    </p>
                  </div>

                  <!-- Phone Number -->
                  <div class="w-full lg:w-1/2">
                    <label
                      class="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                    >
                      Phone Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      :disabled="isSubmitting"
                      v-model="newDelivery.phone"
                      placeholder="+2348000000000"
                      class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-sub-50 focus:border-transparent transition-all duration-200"
                    />
                    <p v-if="errors.phone" class="text-red-500 text-sm mt-1">
                      {{ errors.phone }}
                    </p>
                  </div>
                </div>

                <!-- Email -->
                <div class="w-full">
                  <label
                    class="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                  >
                    Email
                  </label>
                  <input
                    type="text"
                    :disabled="isSubmitting"
                    v-model="newDelivery.email"
                    placeholder="customercare@abc.com"
                    class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-sub-50 focus:border-transparent transition-all duration-200"
                  />
                  <p v-if="errors.email" class="text-red-500 text-sm mt-1">
                    {{ errors.email }}
                  </p>
                </div>

                <!-- Originating Address -->
                <div class="w-full">
                  <label
                    class="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-2"
                  >
                    Originating Address
                  </label>
                  <textarea
                    :disabled="isSubmitting"
                    v-model="newDelivery.address"
                    placeholder="Your shop or office address"
                    class="w-full h-24 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-sub-50 focus:border-transparent transition-all duration-200"
                  ></textarea>
                  <p v-if="errors.address" class="text-red-500 text-sm mt-1">
                    {{ errors.address }}
                  </p>
                </div>

                <!-- Submit Button -->
                <div class="w-full mt-4">
                  <button
                    @click.prevent="startDelivery"
                    type="button"
                    :disabled="isSubmitting || !validateForm"
                    class="w-full bg-main-600 dark:bg-sub-600 text-white py-3 rounded-lg hover:bg-main-700 dark:hover:bg-sub-700 focus:outline-none focus:ring-2 focus:ring-gray-800 dark:focus:ring-sub-50 flex items-center justify-center disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <span v-if="!isSubmitting">Deliver Goods</span>
                    <span v-else class="flex items-center">
                      <!-- Loading SVG Spinner -->
                      <svg
                        class="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        ></circle>
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </span>
                  </button>
                </div>
              </form>
            </div>
            <div
              class="w-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 flex flex-col h-full"
            >
              <!-- Summary Header -->
              <div class="px-4 py-6 md:p-6 xl:p-8">
                <h3 class="text-xl font-semibold text-gray-800 dark:text-white">
                  Summary
                </h3>
              </div>

              <!-- Order Details -->
              <div class="px-4 pb-6 md:px-6 xl:px-8 space-y-4">
                <!-- Subtotal -->
                <div class="flex justify-between items-center w-full">
                  <p class="text-base text-gray-800 dark:text-white truncate">
                    Subtotal
                  </p>
                  <p
                    class="text-base text-gray-600 dark:text-gray-300 truncate"
                  >
                    {{ formatPrice(order.subtotal) }}{{ order.currency }}
                  </p>
                </div>

                <!-- Discount -->
                <div class="flex justify-between items-center w-full">
                  <p class="text-base text-gray-800 dark:text-white truncate">
                    Discount
                    <span
                      class="bg-gray-200 p-1 text-xs font-medium dark:bg-white dark:text-gray-800 ml-2"
                    >
                      N/A
                    </span>
                  </p>
                  <p
                    class="text-base text-gray-600 dark:text-gray-300 truncate"
                  >
                    -0.00 (0%)
                  </p>
                </div>

                <!-- Tax -->
                <div class="flex justify-between items-center w-full">
                  <p class="text-base text-gray-800 dark:text-white truncate">
                    Tax
                  </p>
                  <p
                    class="text-base text-gray-600 dark:text-gray-300 truncate"
                  >
                    +
                    {{
                      formatPrice(
                        (parseFloat(order.tax) / 100) * Number(order.subtotal)
                      )
                    }}
                    ({{ parseFloat(order.tax) }}%){{ order.currency }}
                  </p>
                </div>

                <!-- Delivery -->
                <div class="flex justify-between items-center w-full">
                  <p class="text-base text-gray-800 dark:text-white truncate">
                    Delivery
                  </p>
                  <p
                    class="text-base text-gray-600 dark:text-gray-300 truncate"
                  >
                    {{ formatPrice(order.delivery_cost) }}
                    {{ order.currency }}
                  </p>
                </div>

                <!-- Divider -->
                <div
                  class="border-b border-gray-200 dark:border-gray-700"
                ></div>

                <!-- Total -->
                <div class="flex justify-between items-center w-full pt-4">
                  <p
                    class="text-base font-semibold text-gray-800 dark:text-white truncate"
                  >
                    Total
                  </p>
                  <p
                    class="text-base font-semibold text-gray-600 dark:text-gray-300 truncate"
                  >
                    {{ formatPrice(order.amount) }}
                    {{ order.currency }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            class="w-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 flex flex-col h-full relative landscape:w-2/5 portrait:order-1"
          >
            <!-- Header -->
            <div class="p-6 border-b border-gray-200 dark:border-gray-700">
              <p
                class="text-xl font-semibold text-gray-800 dark:text-white truncate"
              >
                Customer Details
              </p>
            </div>

            <!-- Customer Information -->
            <div class="flex flex-col p-6 space-y-6">
              <!-- Customer Name and Orders -->
              <div class="space-y-2">
                <p
                  class="text-lg font-medium text-gray-800 dark:text-white truncate"
                >
                  {{ order.customer.name }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-300 truncate">
                  {{ order.customer.number_of_orders }} Previous Orders
                </p>
              </div>

              <!-- Email -->
              <div class="flex items-center space-x-3">
                <i class="fas fa-envelope text-lg text-blue-600"></i>
                <a
                  :href="`mailto:${order.customer.email}?subject=Regarding Your Order&body=Hello ${order.customer.name},`"
                  class="text-sm text-blue-600 hover:underline dark:text-blue-400 break-all"
                >
                  {{ order.customer.email }}
                </a>
              </div>

              <!-- WhatsApp -->
              <div class="flex items-center space-x-3">
                <i class="fab fa-whatsapp text-lg text-green-600"></i>
                <a
                  :href="`https://wa.me/${order.customer.phone}?text=Hello%20${encodeURIComponent(order.customer.name)},`"
                  target="_blank"
                  class="text-sm text-green-600 hover:underline dark:text-green-400 break-all"
                >
                  {{ order.customer.phone }}
                </a>
              </div>
            </div>

            <!-- Delivery Details -->
            <div class="p-6 border-t border-gray-200 dark:border-gray-700">
              <p
                class="text-lg font-semibold text-gray-800 dark:text-white mb-4 truncate"
              >
                Delivery Details
              </p>
              <div class="space-y-3">
                <!-- Location -->
                <div>
                  <p
                    class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate"
                  >
                    Location:
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400 break-all">
                    {{ order.delivery_location }}
                  </p>
                </div>

                <!-- Delivery Method -->
                <div>
                  <p
                    class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate"
                  >
                    Delivery Method:
                  </p>
                  <p
                    class="text-sm text-gray-600 dark:text-gray-400 capitalize break-all"
                  >
                    {{ order.delivery_method }}
                  </p>
                </div>

                <!-- Delivery Status -->
                <div>
                  <p
                    class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate"
                  >
                    Delivery Status:
                  </p>
                  <div
                    class="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase rounded-full"
                    :class="statusClass"
                  >
                    {{ currentStatus }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Details -->
            <div class="p-6 border-t border-gray-200 dark:border-gray-700">
              <p
                class="text-lg font-semibold text-gray-800 dark:text-white mb-4 truncate"
              >
                Payment Details
              </p>
              <div class="space-y-3">
                <!-- Payment Method -->
                <div>
                  <p
                    class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate"
                  >
                    Payment Method:
                  </p>
                  <p
                    class="text-sm text-gray-600 dark:text-gray-400 uppercase break-all"
                  >
                    {{ order.payment_method }}
                  </p>
                </div>

                <!-- Payment Status -->
                <div>
                  <p
                    class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate"
                  >
                    Payment Status:
                  </p>
                  <div
                    class="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase rounded-full"
                    :class="
                      order.status === 1
                        ? 'text-green-900 bg-green-500/10 dark:text-green-500 dark:bg-green-500/10'
                        : 'text-red-900 bg-red-500/10 dark:text-red-500 dark:bg-red-500/10'
                    "
                  >
                    {{ order.status === 1 ? "Paid" : "Unpaid" }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

export default {
  name: "OrderDetails",
  inject: ["getFileUrl"],
  data() {
    return {
      notificationSettings: {
        position: "bottom-right",
        transition: "slide",
        autoClose: 1000,
        theme: "auto",
        pauseOnHover: true,
        closeOnClick: true,
      },
      orderId: this.$route.params.id,
      order: {
        customer: {},
        shipping_process: [],
      },
      loading: false,
      isSubmitting: false,
      isLoading: false,
      selectedDelivery: "",
      deliveries: {},
      newDelivery: {
        name: "",
        phone: "",
        email: "",
        address: "",
        orderId: this.$route.params.id,
      },
      errors: {
        name: "",
        phone: "",
        email: "",
        address: "",
      },
    };
  },
  computed: {
    currentStatus() {
      const stages = this.order.shipping_process;

      const completedStages = stages.filter((stage) => stage.date !== null);
      return completedStages.length
        ? completedStages[completedStages.length - 1].process
        : "Pending";
    },
    isPaymentMade() {
      return this.order.status === 1;
    },
    statusClass() {
      return {
        "text-gray-900 bg-gray-100": this.currentStatus === "Pending",
        "text-yellow-900 bg-yellow-100": this.currentStatus === "Order Placed",
        "text-orange-900 bg-orange-100": this.currentStatus === "Order Packed",
        "text-blue-900 bg-blue-100": this.currentStatus === "Processing",
        "text-purple-900 bg-purple-100": this.currentStatus === "Order Shipped",
        "text-green-900 bg-green-100": this.currentStatus === "Order Delivered",
      };
    },
  },
  methods: {
    ...mapActions([
      "fetchOrderDetails",
      "requestConfirmPayment",
      "updateProcessing",
      "submitDelivery",
      "getDeliveries",
    ]),
    updateDelivery() {
      const delivery = this.deliveries.find(
        (delivery) => delivery.id === this.selectedDelivery
      );
      this.newDelivery = {
        name: delivery.name,
        phone: delivery.phone,
        email: delivery.email,
        address: delivery.address,
        orderId: this.$route.params.id,
      };
    },
    async fetchDeliveries() {
      try {
        this.loading = true;
        const response = await this.getDeliveries();
        if (response.success) {
          this.deliveries = response.deliveries;
        }
      } catch (error) {
        console.error("Error fetching deliveries");
      } finally {
        this.loading = false;
      }
    },
    async confirmPayment(orderId) {
      this.isLoading = true;
      try {
        const confirmPayment = this.requestConfirmPayment(orderId).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Update Failed"));
            }
            return data.response;
          }
        );

        toast.promise(
          confirmPayment,
          {
            pending: "Updating payment",
            success: {
              render() {
                return "Payment confirmed";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to confirm payment";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async activateOrder() {
      if (!this.isPaymentMade) {
        return;
      }
      this.loading = true;
      try {
        const response = await this.updateProcessing(this.orderId);
        if (response.success) {
          console.log(response?.message);
        } else {
          console.error(response?.message || "Failed to fetch orders");
        }
      } catch (error) {
        console.error(error || "Failed to fetch orders");
      } finally {
        this.loading = false;
      }
    },
    goback() {
      this.$router.back();
    },
    formatPrice(value) {
      if (value === null || value === undefined || isNaN(parseFloat(value))) {
        return "0.00"; // Default value if invalid
      }
      return parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);

      const options = { day: "numeric", month: "long", year: "numeric" };
      const formattedDate = date.toLocaleDateString("en-GB", options);

      // Get the day with ordinal suffix
      const day = date.getDate();
      const ordinalSuffix = (n) =>
        ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || "th";
      const dayWithSuffix = `${day}${ordinalSuffix(day)}`;

      const timeOptions = { hour: "numeric", minute: "numeric", hour12: true };
      const formattedTime = date.toLocaleTimeString("en-GB", timeOptions);

      return `${dayWithSuffix} ${formattedDate.split(" ")[1]} ${formattedDate.split(" ")[2]} at ${formattedTime}`;
    },

    async fetchOrder() {
      this.loading = true;
      try {
        const response = await this.fetchOrderDetails(this.orderId);
        if (response.success) {
          this.order = response?.data.orders[0];
          const delivery = this.order?.delivery_details;
          this.newDelivery = {
            name: delivery.name,
            phone: delivery.phone,
            email: delivery.email,
            address: delivery.address,
            orderId: this.$route.params.id,
          };
          if (Number(this.order?.shipping_status) < 2) {
            this.activateOrder(this.orderId);
          }
        } else {
          toast.error(response?.error || "Failed to fetch orders");
        }
      } catch (error) {
        console.error("Error fetching orders");
      } finally {
        this.loading = false;
      }
    },
    validateForm() {
      this.errors = {
        name: "",
        phone: "",
        email: "",
        address: "",
      };

      let isValid = true;

      if (!this.newDelivery.name.trim()) {
        this.errors.name = "Delivery Agent is required.";
        isValid = false;
      }

      if (!this.newDelivery.phone.trim()) {
        this.errors.phone = "Phone Number is required.";
        isValid = false;
      }

      if (
        this.newDelivery.email &&
        !this.validateEmail(this.newDelivery.email)
      ) {
        this.errors.email = "Please enter a valid email address.";
        isValid = false;
      }

      if (!this.newDelivery.address.trim()) {
        this.errors.address = "Originating Address is required.";
        isValid = false;
      }

      return isValid;
    },
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    startDelivery() {
      if (!this.validateForm()) {
        return;
      }
      if (!this.isPaymentMade) {
        toast.error("Payment has not been made");
      }
      this.isSubmitting = true;
      try {
        const submitDelivery = this.submitDelivery(this.newDelivery).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Delivery Update Failed!"));
            }
            return data.response;
          }
        );

        toast.promise(
          submitDelivery,
          {
            pending: "Processing Deivery",
            success: {
              render() {
                return "Delivery Processed";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to process delivery";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error(error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  created() {
    this.fetchOrder();
    this.fetchDeliveries();
  },
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
